import { PlusIcon, Search, UserRoundPlus, Users, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import useKeyboardNavigation from "../../hooks/useKeyboardNavigation";

const CustomerSearchInput = ({
  searchInputRef,
  invoiceData,
  searchQuery,
  setSearchQuery,
  suggestions,
  handleAddCustomer,
  handleCustomerSelect,
  showSuggestions,
  handleCustomerList,
  accountData,
  setAccountData,
  loadingCashCustomers,
  loadingCreditCustomers,
  setAccountNumber,
}) => {
  const [glAccount, setGlAccount] = useState(null);
  const [glAccDesc, setGlAccDesc] = useState(null);

  useEffect(() => {
    // Fetch GL Setting for SI-CA-D when component mounts or when company/dept changes
    const fetchGLSetting = async () => {
      try {
        const BASE_URL = localStorage.getItem("apiPaths")
          ? JSON.parse(localStorage.getItem("apiPaths"))[0]?.API_CONFIG
          : "https://cubixweberp.com:213";
        const companyCode = localStorage.getItem("companyCode") || "-";
        const dept =
          localStorage.getItem("currentSelectedDepartment") || "MAIN";
        const screencode = "si";
        const url = `${BASE_URL}/api/CRMGLSettings/${companyCode}/Scrn_code/${screencode}/-/${dept}/-`;
        const response = await fetch(url);
        if (!response.ok) return;
        const data = await response.json();
        if (Array.isArray(data)) {
          const siCaD = data.find((item) => item.Scrn_code === "SI-CA-D");
          if (siCaD) {
            setGlAccount(siCaD.ACCOUNT);
            setGlAccDesc(siCaD.accdesc);
          }
        }
      } catch (e) {
        // Ignore errors for now
      }
    };
    fetchGLSetting();
  }, []);
  useEffect(() => {
    if (!showSuggestions) {
      setHighlightedIndex(-1);
    }
  }, [showSuggestions]);

  const {
    selectedIndex: highlightedIndex,
    setSelectedIndex: setHighlightedIndex,
    rowRefs: suggestionRefs,
  } = useKeyboardNavigation({
    list: suggestions,
    onEnter: handleCustomerSelect,
    isActive: showSuggestions,
  });

  return (
    <>
      <div className=" relative">
        <div className="rounded border border-gray-300 overflow-hidden w-full shadow-sm bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
          <div className="flex items-center divide-x divide-gray-300">
            <div className="bg-gray-100 px-3 py-2 flex items-center">
              <Search className="text-gray-600" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              autoFocus
              placeholder={
                invoiceData.paymentType === "cash"
                  ? "Type to search or Add a new customer"
                  : "Type to search for a customer or Press F2 "
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full py-2 px-3 text-sm bg-white outline-none transition"
            />

            {invoiceData.paymentType === "cash" ? (
              <button
                title="Add new Customer"
                onClick={handleAddCustomer}
                className="bg-gray-50 px-3 py-2 transition text-gray-600 hover:text-gray-700 flex items-center"
              >
                <UserRoundPlus className="w-5 h-5" />
              </button>
            ) : (
              <button
                title="Show Customer List"
                onClick={handleCustomerList}
                className="bg-gray-50 px-3 py-2 transition text-blue-600 hover:text-blue-700 flex items-center"
              >
                <Users className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Search Suggestions list */}
        {showSuggestions && (
          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 border-t-0 rounded-b-md shadow-lg z-10 max-h-52 overflow-y-auto scrollbar-thin">
            {loadingCreditCustomers || loadingCashCustomers ? (
              <div className="px-3 py-4 text-center text-gray-500">
                Loading...
              </div>
            ) : (
              <>
                {suggestions?.map((customer, index) => (
                  <div
                    key={
                      invoiceData.paymentType === "cash"
                        ? customer.AutoId
                        : customer.ACCOUNT
                    }
                    ref={suggestionRefs.current[index]}
                    onClick={() => handleCustomerSelect(customer)}
                    className={`px-3 py-2 flex items-center justify-between cursor-pointer border-b border-gray-100 last:border-b-0 ${
                      highlightedIndex === index
                        ? "bg-gray-100"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <div className="font-medium text-gray-900 text-sm line-clamp-2">
                      {customer.DESCRIPTION}
                    </div>
                    <div className="text-sm text-white px-2 py-0.5 rounded-xl bg-blue-500">
                      {invoiceData.paymentType === "cash"
                        ? customer.AutoId
                        : customer.ACCOUNT}
                    </div>
                  </div>
                ))}

                {/* No customer found (credit) */}
                {invoiceData.paymentType === "credit" &&
                  !suggestions?.length && (
                    <div className="px-3 py-2 text-gray-400 text-center border-t border-gray-200">
                      No Customer Found
                    </div>
                  )}

                {/* Add new customer (cash) */}
                {invoiceData.paymentType === "cash" &&
                  showSuggestions &&
                  searchQuery.trim() !== "" &&
                  (!suggestions || suggestions.length === 0) && (
                    <div
                      onClick={handleAddCustomer}
                      className="px-3 py-3 text-blue-600 hover:text-blue-800 hover:bg-stone-50 flex items-center justify-center gap-2 cursor-pointer font-medium border-t border-gray-200"
                    >
                      <PlusIcon size={20} />
                      Add New Customer
                    </div>
                  )}
              </>
            )}
          </div>
        )}
      </div>

      {accountData && (
        <div className="bg-gradient-to-r from-slate-100 to-gray-100 border border-gray-200 rounded-lg rounded-tr-none mt-2 p-3 shadow-sm relative">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h4
                  title={accountData.accdesc}
                  className="font-semibold text-base text-teal-700 line-clamp-2"
                >
                  {accountData.accdesc || accountData.custref}
                </h4>
              </div>
              <div className="flex items-center gap-1 px-1.5 py-1 bg-blue-600 rounded-full font-medium flex-shrink-0">
                <span className="text-white text-sm">Account Dr:</span>
                <span className=" text-blue-600 bg-white px-2 py-1 rounded-full text-xs font-semibold">
                  {invoiceData.paymentType === "cash"
                    ? glAccount || "-"
                    : accountData.account || accountData.cust_acc || 0}
                </span>
              </div>
            </div>
            <div className="flex items-start justify-between gap-4">
              <p className="text-xs text-gray-500 leading-relaxed flex-1 line-clamp-2">
                {accountData.address1 || accountData.address2}
              </p>
              {/* <span className="text-xs text-white bg-slate-600 px-3 py-1.5 rounded-md flex-shrink-0">
                ID:
                <span className="font-semibold">{accountData.dracc || 0}</span>
              </span> */}
            </div>
          </div>
          <button
            onClick={() => {
              setAccountData(null);
              setSearchQuery("");
              setHighlightedIndex(-1);
              setAccountNumber(null);
            }}
            className=" absolute -top-1.5  border -right-1.5 text-gray-400 hover:text-gray-500  bg-white hover:bg-gray-50 p-1 rounded-full transition-all duration-200 flex-shrink-0"
            title="Remove Selected customer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </>
  );
};

export default CustomerSearchInput;
